import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, Slider, Stack, TextField, Typography, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import { useGrowthPlannerStore } from '../store/growthPlannerStore';
import { SectionHeader, ProgressBar } from '../components/ui';
import { importSkillsFromCv, syncSkillsToCv } from '../utils/cvIntegration';

export default function SkillsPage() {
  const skills = useGrowthPlannerStore((s) => s.skills);
  const addSkill = useGrowthPlannerStore((s) => s.addSkill);
  const updateSkill = useGrowthPlannerStore((s) => s.updateSkill);
  const deleteSkill = useGrowthPlannerStore((s) => s.deleteSkill);
  const importSkills = useGrowthPlannerStore((s) => s.importSkills);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', currentLevel: 1, targetLevel: 3, progress: 0 });
  const [syncMsg, setSyncMsg] = useState('');

  const handleImportFromCv = async () => {
    try {
    const imported = await importSkillsFromCv();
    importSkills(imported);
    setSyncMsg(`Importadas ${imported.length} habilidades desde tu CV activo.`);
    } catch(e) { setSyncMsg(e.response?.data?.message||e.message); }
  };

  const handleSyncToCv = async () => {
    try {
    await syncSkillsToCv(skills.map((s) => s.name));
    setSyncMsg('Skills sincronizadas al CV activo.');
    } catch(e) { setSyncMsg(e.response?.data?.message||e.message); }
  };

  const handleCreate = () => {
    if (!form.name.trim()) return;
    addSkill(form);
    setForm({ name: '', currentLevel: 1, targetLevel: 3, progress: 0 });
    setDialogOpen(false);
  };

  return (
    <Box>
      <SectionHeader
        title="Habilidades"
        subtitle="Sistema reutilizable de skills — conectado con tu CV"
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleImportFromCv}>
              Importar del CV
            </Button>
            <Button variant="outlined" onClick={handleSyncToCv}>Sync al CV</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Nueva Skill
            </Button>
          </Stack>
        }
      />

      {syncMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncMsg('')}>{syncMsg}</Alert>}

      {skills.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2, textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">Leadership, Power BI, React, SQL, UX…</Typography>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={handleImportFromCv}>Importar del CV</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>Agregar manualmente</Button>
          </Stack>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {skills.map((skill) => (
            <Grid item xs={12} sm={6} md={4} key={skill.id}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6" fontWeight={600}>{skill.name}</Typography>
                    <IconButton size="small" color="error" onClick={() => deleteSkill(skill.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Nivel {skill.currentLevel} → {skill.targetLevel}
                  </Typography>
                  <ProgressBar value={skill.progress || 0} label="Progreso" />
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <Box>
                      <Typography variant="caption">Nivel actual</Typography>
                      <Slider
                        value={skill.currentLevel}
                        min={1} max={5} step={1} marks
                        onChange={(_, v) => updateSkill(skill.id, { currentLevel: v })}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption">Nivel objetivo</Typography>
                      <Slider
                        value={skill.targetLevel}
                        min={1} max={5} step={1} marks
                        onChange={(_, v) => updateSkill(skill.id, { targetLevel: v })}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption">Progreso %</Typography>
                      <Slider
                        value={skill.progress || 0}
                        min={0} max={100}
                        onChange={(_, v) => updateSkill(skill.id, { progress: v })}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nueva Habilidad</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre" placeholder="Ej: Power BI" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth autoFocus />
            <TextField label="Nivel actual" type="number" inputProps={{ min: 1, max: 5 }} value={form.currentLevel} onChange={(e) => setForm({ ...form, currentLevel: Number(e.target.value) })} fullWidth />
            <TextField label="Nivel objetivo" type="number" inputProps={{ min: 1, max: 5 }} value={form.targetLevel} onChange={(e) => setForm({ ...form, targetLevel: Number(e.target.value) })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.name.trim()}>Agregar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
